import { defaultTagColorOption, getLocalizedStringValue } from '@griply/utils';
import React, { useMemo } from 'react';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { NewGoalWithID } from 'shared/types/goal';
import { LifeArea, LocalizedLifeArea } from 'shared/types/life-area';
import { NewTagWithID } from 'shared/types/tag';
import { v4 as uuidv4 } from 'uuid';

import { DataContext, DataContextProps } from './data-context';
import { TaskCreate, TaskCreateProps } from './task-create';
import { TaskDetail, TaskDetailProps } from './task-detail';

type ContextualCreationData = Omit<
  DataContextProps['data'],
  'lifeAreas' | 'goalsToCreate' | 'tagsToCreate'
> & {
  lifeAreas: (LifeArea | LocalizedLifeArea)[];
};

export type DetailDataProviderProps = TaskDetailProps & {
  contextualCreationData: ContextualCreationData;
};

export const DetailDataProvider: React.FC<DetailDataProviderProps> = ({
  contextualCreationData,
  ...rest
}) => {
  const locale = useI18nLocale();

  const [goalsToCreate, setGoalsToCreate] = React.useState<NewGoalWithID[]>([]);
  const [tagsToCreate, setTagsToCreate] = React.useState<NewTagWithID[]>([]);

  const addGoalToCreate = (name: string) =>
    setGoalsToCreate((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name,
      },
    ]);

  const addTagToCreate = (name: string) =>
    setTagsToCreate((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name,
        color: defaultTagColorOption.value,
      },
    ]);

  const _goals = useMemo(
    () => [...goalsToCreate, ...contextualCreationData.goals],
    [contextualCreationData.goals, goalsToCreate],
  );
  const _tags = useMemo(
    () => [...tagsToCreate, ...contextualCreationData.tags],
    [contextualCreationData.tags, tagsToCreate],
  );

  const lifeAreas = useMemo(
    () =>
      contextualCreationData.lifeAreas.map(({ name, ...rest }) => ({
        ...rest,
        name: getLocalizedStringValue(name, locale),
      })),
    [contextualCreationData.lifeAreas, locale],
  );

  const value = useMemo(
    () => ({
      data: {
        ...contextualCreationData,
        lifeAreas,
        goals: _goals,
        tags: _tags,
        goalsToCreate,
        tagsToCreate,
      },
      addGoalToCreate,
      addTagToCreate,
    }),
    [
      _goals,
      _tags,
      contextualCreationData,
      goalsToCreate,
      lifeAreas,
      tagsToCreate,
    ],
  );

  return (
    <DataContext.Provider value={value}>
      <TaskDetail {...rest} />
    </DataContext.Provider>
  );
};

export type CreateDataProviderProps = TaskCreateProps & {
  contextualCreationData: ContextualCreationData;
};

export const CreateDataProvider: React.FC<CreateDataProviderProps> = ({
  contextualCreationData,
  ...rest
}) => {
  const locale = useI18nLocale();

  const [goalsToCreate, setGoalsToCreate] = React.useState<NewGoalWithID[]>([]);
  const [tagsToCreate, setTagsToCreate] = React.useState<NewTagWithID[]>([]);

  const addGoalToCreate = (name: string) =>
    setGoalsToCreate((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name,
      },
    ]);

  const addTagToCreate = (name: string) =>
    setTagsToCreate((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name,
        color: defaultTagColorOption.value,
      },
    ]);

  const _goals = useMemo(
    () => [...goalsToCreate, ...contextualCreationData.goals],
    [contextualCreationData.goals, goalsToCreate],
  );
  const _tags = useMemo(
    () => [...tagsToCreate, ...contextualCreationData.tags],
    [contextualCreationData.tags, tagsToCreate],
  );

  const lifeAreas = useMemo(
    () =>
      contextualCreationData.lifeAreas.map(({ name, ...rest }) => ({
        ...rest,
        name: getLocalizedStringValue(name, locale),
      })),
    [contextualCreationData.lifeAreas, locale],
  );

  const value = useMemo(
    () => ({
      data: {
        ...contextualCreationData,
        lifeAreas,
        goals: _goals,
        tags: _tags,
        goalsToCreate,
        tagsToCreate,
      },
      addGoalToCreate,
      addTagToCreate,
    }),
    [
      _goals,
      _tags,
      contextualCreationData,
      goalsToCreate,
      lifeAreas,
      tagsToCreate,
    ],
  );

  return (
    <DataContext.Provider value={value}>
      <TaskCreate {...rest} />
    </DataContext.Provider>
  );
};
